/* eslint-disable react/prop-types, import/no-extraneous-dependencies, no-console */

import React from 'react';

import PropTypes from 'prop-types';
import { Form, Button, Select, Loader, Message, Checkbox } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import API from '../../../utils/api';
import ErrorHandler from '../../../utils/errors';
import { validateEmail } from '../../../utils/validators';
import BusinessSearchField from '../../BusinessSearchField';
import {
  ADDRESS_TYPES,
  CANT_FIND_ADDRESS_TITLE,
} from '../../BusinessSearchField/BusinessSearchField.constants';
import PhoneInput from '../../PhoneInput/PhoneInput';
import RecaptchaForm from '../../RecaptchaForm';
import { ActionName } from '../../RecaptchaProvider/RecaptchaProvider.constants';
import PostSubmissionMessage from '../PostSubmissionMessage';
import {
  BUSINESS_ADDRESS_NOT_SELECTED,
  BUSINESS_NOT_FILLED,
  BUSINESS_NOT_SELECTED,
  INVALID_EMAIL,
  DEFAULT_REFERRAL_SOURCE,
} from './SubmissionForm.constants';
import { extractReferralEmail, getUtmValues } from './SubmissionForm.utils';

import './SubmissionForm.css';

const propTypes = {
  postSubmissionMessage: PropTypes.string.isRequired,
  referralSource: PropTypes.string,
  marketingOptIn: PropTypes.bool,
};

const defaultProps = {
  referralSource: DEFAULT_REFERRAL_SOURCE,
  marketingOptIn: false,
};

export default class SubmissionForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sent: false,
      manualAddress: false,
      placeId: '',
      loadingAddressComponents: false,
      touched: false,
      emailFields: this.getEmailFields(),
      ...this.createFormStateFields(props.fields),
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onResetForm = this.onResetForm.bind(this);
    this.onBusinessNameSearchSelected = this.onBusinessNameSearchSelected.bind(this);
    this.toggleManualAddress = this.toggleManualAddress.bind(this);
    this.setBusinessAddressComponents = this.setBusinessAddressComponents.bind(this);
    this.onAddressSearchSelected = this.onAddressSearchSelected.bind(this);
  }

  handleChange(e, { name, value }) {
    this.setState({ [name]: value });
  }

  handleCheckboxChange(e, { name, checked }) {
    this.setState({ [name]: checked });
  }

  async onBusinessNameSearchSelected(result) {
    const { title, place_id: resultPlaceId } = result;

    await this.setBusinessAddressComponents(resultPlaceId);

    this.setState({
      businessName: title.trim(),
      placeId: resultPlaceId,
    });
  }

  async onAddressSearchSelected(result) {
    const { title, place_id: resultPlaceId } = result;

    await this.setBusinessAddressComponents(resultPlaceId, { setAddress: false });

    this.setState({ businessAddress: title.trim() });
  }

  async onSubmit(e, recaptchaToken) {
    const { referralSource } = this.props;
    const {
      contactName,
      businessName,
      businessType,
      contactEmail,
      contactPhone,
      businessStreet,
      businessCity,
      businessState,
      businessZip,
      placeId,
      discoverySource,
      marketingOptIn,
    } = this.state;

    const splitNames = contactName.split(' ');
    const firstName = _get(splitNames, '[0]', '');
    const lastName = _get(splitNames, '[1]', '');
    const referralEmail = extractReferralEmail(this.state);

    this.setState({ touched: true });

    if (!_isEmpty(this.getFormErrorMessages())) {
      return;
    }

    try {
      const payload = {
        lead_type: 'SQL',
        business_name: businessName,
        business_category: businessType,
        business_owner_name: `${firstName} ${lastName}`,
        business_owner_email: contactEmail,
        business_owner_phone_number: contactPhone,
        business_street: businessStreet,
        business_city: businessCity,
        business_state: businessState,
        business_zip_code: businessZip,
        business_validation_id: placeId,
        referral_email: referralEmail,
        referral_source: referralSource,
        marketing_opt_in: marketingOptIn,
        discovery_source: discoverySource,
        tracking_metadata: getUtmValues(),
      };

      await API.submitLead(payload, recaptchaToken);

      this.setState({ sent: true });
    } catch (error) {
      console.error(error);
    }
  }

  onResetForm() {
    this.setState({
      sent: false,
      manualAddress: false,
      touched: false,
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      businessName: '',
      businessAddress: '',
      businessCity: '',
      businessState: '',
      businessZip: '',
      placeId: '',
    });
  }

  async setBusinessAddressComponents(placeId, { setAddress = true } = {}) {
    try {
      this.setState({ loadingAddressComponents: true });
      const { data: placeDetails } = await API.searchPlaceDetails(placeId);
      const {
        address: businessAddress,
        street: businessStreet,
        city: businessCity,
        state: businessState,
        postal_code: businessZip,
      } = placeDetails;

      this.setState({
        ...{
          businessStreet,
          businessState,
          businessZip,
          businessCity,
        },
        ...(setAddress ? { businessAddress } : null),
      });
    } catch (e) {
      ErrorHandler.capture(e);
    } finally {
      this.setState({ loadingAddressComponents: false });
    }
  }

  getComponent(fieldProps) {
    const { type, name, label, required } = fieldProps;
    const {
      [name]: value,
      manualAddress,
      businessName,
      businessCity,
      businessZip,
      businessState,
      loadingAddressComponents,
      businessAddress,
      touched,
    } = this.state;

    if (type === 'select') {
      const { options = [], placeholder } = fieldProps;

      return (
        <Form.Field>
          <label>{label}</label>
          <Select
            name={name}
            onChange={this.handleChange}
            options={options}
            placeholder={placeholder}
            search
          />
        </Form.Field>
      );
    }

    if (type === 'phoneInput') {
      return (
        <Form.Field required={required}>
          <label>{label}</label>
          <PhoneInput
            onChange={(e, phoneInputValue) =>
              this.handleChange(e, { name, value: phoneInputValue })
            }
            value={value}
            {...fieldProps}
          />
        </Form.Field>
      );
    }

    if (type === 'businessSearch') {
      return (
        <>
          {!manualAddress && (
            <BusinessSearchField
              onResultSelected={this.onBusinessNameSearchSelected}
              setNoResultsFound={this.toggleManualAddress}
              error={!this.isValidBusinessName() && touched}
              {...fieldProps}
            />
          )}
          {manualAddress && (
            <>
              <Form.Input
                onChange={this.handleChange}
                error={!this.isValidBusinessName() && touched}
                value={businessName}
                {...fieldProps}
              />

              <BusinessSearchField
                onResultSelected={this.onAddressSearchSelected}
                setNoResultsFound={this.toggleManualAddress}
                types={ADDRESS_TYPES}
                label="Business Address"
                placeholder="Enter Business Address"
                required
                cantFindTitle={CANT_FIND_ADDRESS_TITLE}
                error={!this.isValidBusinessAddress() && touched}
              />

              <Loader inline active={loadingAddressComponents} />

              {!loadingAddressComponents && businessAddress && (
                <Form.Group widths="equal">
                  <Form.Input
                    required
                    placeholder="City"
                    name="businessCity"
                    value={businessCity}
                    onChange={this.handleChange}
                  />
                  <Form.Input
                    required
                    placeholder="State"
                    name="businessState"
                    value={businessState}
                    onChange={this.handleChange}
                  />
                  <Form.Input
                    required
                    placeholder="Postal Code"
                    name="businessZip"
                    value={businessZip}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              )}
            </>
          )}
        </>
      );
    }

    let additionalProps = {};

    if (type === 'email') {
      additionalProps = { error: !this.isValidEmail(name) && touched };
    }

    return (
      <Form.Input
        onChange={this.handleChange}
        fluid
        value={value}
        {...fieldProps}
        {...additionalProps}
      />
    );
  }

  getEmailFields() {
    const { fields } = this.props;
    return fields.filter(({ type }) => type === 'email').map(({ name }) => name);
  }

  getFormErrorMessages() {
    const { manualAddress, emailFields } = this.state;
    let messages = [];

    emailFields.forEach((emailField) => {
      if (!this.isValidEmail(emailField)) {
        messages = [...messages, INVALID_EMAIL];
      }
    });

    if (!this.isValidBusinessName()) {
      if (!manualAddress) {
        return [...messages, BUSINESS_NOT_SELECTED];
      }
      messages = [...messages, BUSINESS_NOT_FILLED];
    }
    if (!this.isValidBusinessAddress()) {
      messages = [...messages, BUSINESS_ADDRESS_NOT_SELECTED];
    }
    return messages;
  }

  isValidBusinessName() {
    const { businessName } = this.state;
    return !_isEmpty(businessName);
  }

  isValidBusinessAddress() {
    const { businessAddress, placeId } = this.state;
    return !_isEmpty(placeId) || !_isEmpty(businessAddress);
  }

  isValidEmail(fieldName) {
    const { [fieldName]: value } = this.state;
    const { fields } = this.props;
    const field = fields.find((f) => f.name === fieldName);

    return validateEmail(value, field.required);
  }

  toggleManualAddress() {
    this.setState(({ manualAddress }) => {
      return {
        manualAddress: !manualAddress,
        businessName: '',
        businessAddress: '',
        businessState: '',
        businessZip: '',
        businessCity: '',
        placeId: '',
      };
    });
  }

  createFormStateFields(fields) {
    return fields.reduce(
      (state, { name, fieldGroup }) => {
        if (fieldGroup) return { ...state, ...this.createFormStateFields(fieldGroup) };
        return { ...state, [name]: '' };
      },
      { marketingOptIn: false },
    );
  }

  render() {
    const {
      fields,
      buttonMessage,
      postSubmissionMessage,
      postSubmissionButtonMessage,
      style,
      marketingOptIn,
    } = this.props;
    const {
      sent,
      loadingAddressComponents,
      touched,
      marketingOptIn: marketingOptInState,
    } = this.state;

    const errorMessages = touched ? this.getFormErrorMessages() : [];

    return (
      <div className="intake-form" style={style}>
        {sent ? (
          <PostSubmissionMessage
            message={postSubmissionMessage}
            buttonName={postSubmissionButtonMessage}
            onReset={this.onResetForm}
          />
        ) : (
          <RecaptchaForm onSubmit={this.onSubmit} action={ActionName.SUBMIT_LEAD}>
            {({ submitting }) => (
              <>
                {fields.map(({ fieldGroup, ...fieldProps }) => (
                  <React.Fragment key={JSON.stringify({ ...fieldGroup, ...fieldProps })}>
                    {fieldGroup ? (
                      <Form.Group widths="equal">
                        {fieldGroup.map((childFieldProps) => (
                          <React.Fragment key={childFieldProps.name}>
                            {this.getComponent(childFieldProps)}
                          </React.Fragment>
                        ))}
                      </Form.Group>
                    ) : (
                      this.getComponent(fieldProps)
                    )}
                  </React.Fragment>
                ))}
                {marketingOptIn && (
                  <Form.Field>
                    <Checkbox
                      checked={marketingOptInState}
                      name="marketingOptIn"
                      onChange={this.handleCheckboxChange}
                      label="I would like to receive communication from Fisherman with content, tips, and product updates to help grow my business."
                    />
                  </Form.Field>
                )}
                <Button
                  type="submit"
                  size="small"
                  disabled={submitting || !_isEmpty(errorMessages) || loadingAddressComponents}
                  loading={submitting}
                  primary
                  fluid
                >
                  <h4>{sent ? 'Submitted' : buttonMessage}</h4>
                </Button>
                <p>
                  By clicking the submit button, I agree that I have read the{' '}
                  <a
                    href="https://www.gofisherman.com/legal/privacy-policy"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    privacy policy
                  </a>
                  , and will receive sales communication from Fisherman Websites.
                </p>
                <Message error visible={!_isEmpty(errorMessages)}>
                  <Message.List>
                    {errorMessages.map((message) => (
                      <Message.Item>{message}</Message.Item>
                    ))}
                  </Message.List>
                </Message>
              </>
            )}
          </RecaptchaForm>
        )}
      </div>
    );
  }
}

SubmissionForm.propTypes = propTypes;
SubmissionForm.defaultProps = defaultProps;
